import addDaysFns from 'date-fns/addDays';
import addMinutesFns from 'date-fns/addMinutes';
import addMonthFns from 'date-fns/addMonths';
import addSecondsFns from 'date-fns/addSeconds';
import addWeeksFns from 'date-fns/addWeeks';
import addYearsFns from 'date-fns/addYears';
import compareAsc from 'date-fns/compareAsc';
import differenceInDaysFns from 'date-fns/differenceInDays';
import differenceInHoursFns from 'date-fns/differenceInHours';
import differenceInMinutesFns from 'date-fns/differenceInMinutes';
import differenceInSecondsFns from 'date-fns/differenceInSeconds';
import endOfDayFns from 'date-fns/endOfDay';
import endOfMonthFns from 'date-fns/endOfMonth';
import endOfTodayFns from 'date-fns/endOfToday';
import formatFns from 'date-fns/format';
import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import formatISO from 'date-fns/formatISO';
import endOfWeekWithOptions from 'date-fns/fp/endOfWeekWithOptions';
import startOfWeekWithOptionsFns from 'date-fns/fp/startOfWeekWithOptions';
import getDaysInMonth from 'date-fns/getDaysInMonth';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import isSameDay from 'date-fns/isSameDay';
import isValidFns from 'date-fns/isValid';
import isWithinInterval from 'date-fns/isWithinInterval';
import {ru} from 'date-fns/locale';
import max from 'date-fns/max';
import min from 'date-fns/min';
import parseFns from 'date-fns/parse';
import startOfDayFns from 'date-fns/startOfDay';
import startOfMonthFns from 'date-fns/startOfMonth';
import startOfTodayFns from 'date-fns/startOfToday';
import {DatePeriod, Period} from './types';

export const DAYS_IN_WEEK = 7;
export const SHORT_WEEK_NAMES = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

function isValid(date: unknown) {
	return isValidFns(date);
}

function format(date: Date, format = 'dd.MM.yyyy в HH:mm', {locale = ru}: {locale?: Locale} = {}) {
	return formatFns(date, format, {locale});
}

function differenceInDays(leftDate: Date, rightDate: Date = new Date()) {
	return differenceInDaysFns(leftDate, rightDate);
}

function differenceInHours(leftDate: Date, rightDate: Date = new Date()) {
	return differenceInHoursFns(leftDate, rightDate);
}

function differenceInMinutes(leftDate: Date, rightDate: Date = new Date()) {
	return differenceInMinutesFns(leftDate, rightDate);
}

function differenceInSeconds(leftDate: Date, rightDate: Date = new Date()) {
	return differenceInSecondsFns(leftDate, rightDate);
}

const defaultFormatOptions = {
	locale: ru,
	addSuffix: true,
};

function formatDistance(
	date: Date,
	baseDate: Date = new Date(),
	options: {
		locale?: Locale;
		addSuffix?: boolean;
		unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
		roundingMethod?: 'floor' | 'ceil' | 'round';
	} = {},
) {
	return formatDistanceStrict(date, baseDate, {...defaultFormatOptions, ...options});
}

function parse(
	dateString: string,
	formatSting: string,
	targetDate: Date = new Date(),
	{locale = ru}: {locale?: Locale} = {},
) {
	const date = parseFns(dateString, formatSting, targetDate, {locale});
	return isValid(date) ? date : null;
}

const startOf = {
	today: startOfTodayFns,
	day: startOfDayFns,
	week: startOfWeekWithOptionsFns({weekStartsOn: 1, locale: ru}),
	month: startOfMonthFns,
};

const endOf = {
	today: endOfTodayFns,
	day: endOfDayFns,
	week: endOfWeekWithOptions({weekStartsOn: 1, locale: ru}),
	month: endOfMonthFns,
};

const add = {
	seconds: addSecondsFns,
	minutes: addMinutesFns,
	days: addDaysFns,
	weeks: addWeeksFns,
	month: addMonthFns,
	years: addYearsFns,
};

const period = {
	parse(period: string | string[]): DatePeriod {
		const [start, end] = Array.isArray(period) ? period : period.split(':');

		const startDate = parse(start, 'dd-MM-yyyy');
		const endDate = parse(end, 'dd-MM-yyyy');

		return [startDate && startOf.day(startDate), endDate && endOf.day(endDate)];
	},
	stringify(period: DatePeriod): string | undefined {
		const stringifiedPeriod = period.map((p) => (p !== null ? format(p, 'dd-MM-yyyy') : ''));

		return stringifiedPeriod.filter(Boolean).length !== 0 ? stringifiedPeriod.join(':') : undefined;
	},
	getToday(): DatePeriod {
		return [startOf.today(), endOf.today()];
	},
	getYesterday(): DatePeriod {
		return [add.days(startOf.today(), -1), add.days(endOf.today(), -1)];
	},
	getLastDays(shift = 1): DatePeriod {
		return [add.days(startOf.today(), -shift), endOf.today()];
	},
	getLastMonths(shift = 1): DatePeriod {
		return [add.month(startOf.today(), -shift), endOf.today()];
	},
	getLastYears(shift = 1): DatePeriod {
		return [add.years(startOf.today(), -shift), endOf.today()];
	},
	isEqual([leftStart, leftEnd]: DatePeriod, [rightStart, rightEnd]: DatePeriod): boolean {
		return (
			(leftStart && rightStart ? isEqual(leftStart, rightStart) : leftStart === rightStart) &&
			(leftEnd && rightEnd ? isEqual(leftEnd, rightEnd) : leftEnd === rightEnd)
		);
	},
	isToday(period: DatePeriod): boolean {
		return this.isEqual(period, this.getToday());
	},
	isYesterday(period: DatePeriod): boolean {
		return this.isEqual(period, this.getYesterday());
	},
	isLastDays(period: DatePeriod, shift?: number): boolean {
		return this.isEqual(period, this.getLastDays(shift));
	},
	isLastMonths(period: DatePeriod, shift?: number): boolean {
		return this.isEqual(period, this.getLastMonths(shift));
	},
	isLastYears(period: DatePeriod, shift?: number): boolean {
		return this.isEqual(period, this.getLastYears(shift));
	},
	isWithin(date: Date, period: DatePeriod): boolean {
		const [start, end] = period;

		if (!start || !end) return false;
		if (isAfter(start, end)) return false;

		return isWithinInterval(date, {start, end});
	},
};

function getWeekDay(date: Date, weekStartsOn = 1) {
	const day = date.getDay();
	return (day - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK;
}

const minutes = {
	toMilliseconds: (value: number) => value * 60_000,
};

export const DATE_FORMAT = 'dd.MM.yyyy';

function readAsDatePeriod(period: Period | DatePeriod): DatePeriod {
	if (Array.isArray(period)) return period;

	return [period.startDate, period.endDate];
}

export function formatPeriodLabel(period: Period | DatePeriod, defaultValue = ''): string {
	const [startDate, endDate] = readAsDatePeriod(period);
	const formatDate = (date: Date) => format(date, DATE_FORMAT);

	if (startDate && endDate) return `${formatDate(startDate)} - ${formatDate(endDate)}`;
	if (startDate) return `от ${formatDate(startDate)}`;
	if (endDate) return `до ${formatDate(endDate)}`;

	return defaultValue;
}

function earliest(date: Date, fallback: Date): Date {
	return isBefore(date, fallback) ? date : fallback;
}

export const date = {
	format,
	formatDistance,
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInSeconds,
	parse,
	isValid,
	startOf,
	endOf,
	add,
	period,
	isEqual,
	formatISO,
	getDaysInMonth,
	getWeekDay,
	isSameDay,
	compareAsc,
	isBefore,
	isAfter,
	max,
	min,
	minutes,
	earliest,
};
